import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { istAusSchlafecke } from '../features/schlaf/schlafecke';
import { istAusBuehne } from './buehne';
import { Icon, type IconName } from './Icon';
import styles from './Sheet.module.css';

/**
 * Bodenblatt für die großen Auswahllisten (Prüfung einrichten, Prüfling wechseln).
 *
 * Wie beim `Dialog` liefert Radix nur das Verhalten — Fokus-Käfig, Escape, Scroll-Sperre,
 * ARIA. Der Unterschied ist die Platzierung: das Blatt sitzt unten und lässt oben einen
 * Streifen des Bogens stehen, damit erkennbar bleibt, worüber es liegt.
 */
export function Sheet({
  kicker,
  kickerIcon,
  title,
  onClose,
  actions,
  mitte,
  children,
}: {
  kicker: string;
  kickerIcon: IconName;
  title: string;
  onClose: () => void;
  /** Knöpfe rechts in der Kopfzeile. */
  actions?: ReactNode;
  /**
   * Ein Knopf **in der Mitte** der Kopfzeile — für den einen Weg, der nicht zu den Aktionen
   * am Rand gehört, weil er nichts bestätigt und nichts schliesst, sondern die Wahl auf einem
   * anderen Weg beantwortet (der QR-Scan in der Prüfungsauswahl, Vorgabe des Nutzers vom
   * 2026-09-14).
   *
   * Ist er gesetzt, wird die Kopfzeile zum Raster `1fr auto 1fr`: der Knopf sitzt dann in der
   * **Mitte der Kopfzeile**, nicht nur zwischen Titel und Aktionen. Ohne ihn bleibt die
   * Kopfzeile, wie sie war.
   */
  mitte?: ReactNode;
  children: ReactNode;
}) {
  /**
   * Radix meldet jeden Tipper ausserhalb des Blattes und schlösse es. Der Schlafknopf liegt
   * dort — er ist aber Bedienung und kein Weg nach draussen (s. `schlafecke.ts`).
   */
  const ausserhalb = (e: { detail: { originalEvent: Event }; preventDefault: () => void }) => {
    const ziel = e.detail.originalEvent.target;
    if (istAusSchlafecke(ziel) || istAusBuehne(ziel)) e.preventDefault();
  };

  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.overlay} />
        {/* Ein Tipper auf den Schlafknopf ist für Radix ein Klick „nach draussen" und schlösse
            das Blatt. Er gehört aber zur Bedienung — s. `features/schlaf/schlafecke.ts`. */}
        {/* Radix setzt beim Öffnen von sich aus den Fokus auf das erste bedienbare Element.
            Auf einem Tastaturgerät ist das richtig; auf dem iPad hinterlässt es einen
            Fokusrahmen auf einem Knopf, den niemand angefasst hat. Der Fokus bleibt im Blatt
            gefangen (das macht Radix weiter), er wird nur nicht mehr vorab gesetzt. */}
        <RadixDialog.Content
          className={styles.content}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={ausserhalb}
          onInteractOutside={ausserhalb}
        >
          <div className={styles.head} data-mitte={mitte ? 'true' : undefined}>
            <div className={styles.headText}>
              <div className={styles.kicker}>
                <Icon name={kickerIcon} size={14} />
                <span>{kicker}</span>
              </div>
              <RadixDialog.Title className={styles.title}>{title}</RadixDialog.Title>
              <RadixDialog.Description hidden>{kicker}</RadixDialog.Description>
            </div>
            {mitte && <div className={styles.headMitte}>{mitte}</div>}
            {/* Die Aktionen liegen in einer eigenen Hülle, damit sie im Raster **eine** Spalte
                belegen statt je Knopf eine. Ihr Abstand ist derselbe wie vorher der der
                Kopfzeile — am Erscheinungsbild der Blätter ohne Mitte ändert das nichts. */}
            <div className={styles.headActions}>{actions}</div>
          </div>
          <div className={styles.body}>{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Knopf für die Kopfzeile eines Blattes. */
export function SheetAction({
  icon,
  label,
  onClick,
  variant = 'plain',
  disabled = false,
  iconOnly = false,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  variant?: 'plain' | 'outline' | 'primary';
  /** Gesperrt, solange die Wahl unvollständig ist — s. `SetupSheet` im Startablauf. */
  disabled?: boolean;
  /**
   * Nur das Symbol, quadratisch. Für das Schliessen: das Kreuz ist überall dasselbe und
   * braucht die Breite eines beschrifteten Knopfes nicht. `label` bleibt Pflicht und wird
   * zur Vorlesebeschriftung — ein Knopf ohne Namen ist für Vorleseprogramme stumm.
   */
  iconOnly?: boolean;
}) {
  return (
    <button
      type="button"
      className={`tap ${styles.action}`}
      data-variant={variant}
      data-icon-only={iconOnly}
      disabled={disabled}
      onClick={onClick}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
    >
      <Icon name={icon} size={iconOnly ? 18 : 16} />
      {!iconOnly && <span>{label}</span>}
    </button>
  );
}
